import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3, Users, Mail, CreditCard, ClipboardList, Inbox, MessageSquare,
  BookOpen, GraduationCap, Gift, Layers, PenSquare, Image as ImageIcon,
  Palette, Settings as SettingsIcon, Menu, X, ChevronRight, LogOut,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Grouped sidebar navigation. Each item's `to` is a real URL — either an
// in-shell section (rendered inside Admin.jsx) or a dedicated admin page
// like Student Progress / Page Images. All render inside <AdminShell> so
// the sidebar stays visible.
export const ADMIN_NAV = [
  {
    label: "Dashboard",
    items: [
      { id: "overview", label: "Pending Submissions", to: "/admin", icon: BarChart3, exact: true },
    ],
  },
  {
    label: "People",
    items: [
      { id: "users", label: "Users", to: "/admin/users", icon: Users },
      { id: "newsletter", label: "Newsletter", to: "/admin/newsletter", icon: Mail },
    ],
  },
  {
    label: "Commerce & Leads",
    items: [
      { id: "registrations", label: "Training & Retreat Registrations", to: "/admin/registrations", icon: ClipboardList, badgeKey: "registrations" },
      { id: "event_registrations", label: "Event Registrations", to: "/admin/event-registrations", icon: ClipboardList },
      { id: "bookings", label: "Retreat Bookings", to: "/admin/bookings", icon: CreditCard },
      { id: "reiki_bookings", label: "Reiki Bookings", to: "/admin/reiki", icon: CreditCard },
      { id: "inquiries", label: "Studio Inquiries", to: "/admin/inquiries", icon: Inbox, badgeKey: "inquiries" },
      { id: "trainer_applications", label: "Trainer Applications", to: "/admin/trainer-applications", icon: Inbox },
      { id: "contact", label: "Contact Messages", to: "/admin/contact", icon: MessageSquare, badgeKey: "contact" },
    ],
  },
  {
    label: "LMS",
    items: [
      { id: "progress", label: "Student Progress", to: "/admin/lms/progress", icon: GraduationCap, badgeKey: "progress" },
      { id: "courses", label: "Courses & Quizzes", to: "/admin/courses", icon: BookOpen },
      { id: "comp_access", label: "Grant Course Access", to: "/admin/comp-access", icon: Gift },
    ],
  },
  {
    label: "Content",
    items: [
      { id: "offerings", label: "Offerings", to: "/admin/offerings", icon: Layers },
      { id: "journal", label: "Journal", to: "/admin/journal", icon: PenSquare },
      { id: "photos", label: "Photos", to: "/admin/photos", icon: ImageIcon },
      { id: "page_images", label: "Page Images", to: "/admin/page-images", icon: Palette },
    ],
  },
  {
    label: "System",
    items: [
      { id: "settings", label: "Settings", to: "/admin/settings", icon: SettingsIcon },
    ],
  },
];

// Poll a lightweight counts endpoint so pending items surface as badges next
// to each nav item. Refreshes every 60s so counts stay reasonably fresh
// without Holly needing to reload.
function useAdminBadges() {
  const [badges, setBadges] = useState({});
  useEffect(() => {
    let alive = true;
    const fetchCounts = async () => {
      try {
        const { data } = await api.get("/admin/nav-counts");
        if (alive) setBadges(data || {});
      } catch { /* silent; sidebar still works without badges */ }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return badges;
}

function NavItem({ item, badges, onNavigate }) {
  const Icon = item.icon;
  const count = badges[item.badgeKey];
  return (
    <NavLink
      to={item.to}
      end={item.exact || false}
      onClick={onNavigate}
      className={({ isActive }) =>
        `group flex items-center gap-3 pl-4 pr-3 py-2 rounded-lg text-[13px] transition-all ${
          isActive
            ? "bg-aviana-plum/10 text-aviana-plum font-medium"
            : "text-aviana-inkSoft hover:bg-aviana-surface/70 hover:text-aviana-ink"
        }`
      }
      data-testid={`admin-nav-${item.id}`}
    >
      {({ isActive }) => (
        <>
          <Icon size={15} className={isActive ? "text-aviana-plum" : "text-aviana-muted group-hover:text-aviana-goldDeep"} />
          <span className="flex-1 truncate">{item.label}</span>
          {count > 0 && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold tabular-nums ${
                isActive ? "bg-aviana-plum text-aviana-cream" : "bg-aviana-gold/20 text-aviana-goldDeep"
              }`}
              data-testid={`admin-nav-badge-${item.id}`}
            >
              {count}
            </span>
          )}
          {isActive && <ChevronRight size={12} className="text-aviana-plum -mr-1" />}
        </>
      )}
    </NavLink>
  );
}

export default function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const badges = useAdminBadges();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever the route changes so Holly doesn't have
  // to hit the X after every tap.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const currentSection = useMemo(() => {
    for (const group of ADMIN_NAV) {
      for (const it of group.items) {
        if (it.exact ? location.pathname === it.to : location.pathname === it.to || location.pathname.startsWith(it.to + "/")) {
          return { group: group.label, item: it };
        }
      }
    }
    return null;
  }, [location.pathname]);

  const handleLogout = async () => {
    // Navigate first so we leave /admin before AuthContext flips `user` to
    // false — otherwise Admin.jsx's own auth guard races and lands us on
    // /login instead of the homepage.
    nav("/");
    try { await logout(); } catch { /* nav already happened */ }
  };

  return (
    <div className="pt-24 min-h-screen bg-aviana-paper/40" data-testid="admin-shell">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-24 z-30 bg-aviana-cream/95 backdrop-blur border-b border-aviana-border px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setMobileOpen(true)}
          className="flex items-center gap-2 text-[13px] text-aviana-ink"
          data-testid="admin-mobile-menu-open"
        >
          <Menu size={18} /> Menu
        </button>
        <p className="text-[12px] tracking-[0.18em] uppercase text-aviana-plum">
          {currentSection?.item?.label || "Admin"}
        </p>
        <div className="w-10" />
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-aviana-ink/50 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="admin-mobile-drawer-scrim"
        />
      )}

      <div className="flex">
        {/* Sidebar — persistent desktop, drawer mobile */}
        <aside
          className={`fixed lg:sticky lg:top-24 top-0 left-0 h-screen lg:h-[calc(100vh-6rem)] w-72 bg-aviana-cream border-r border-aviana-border z-50 transition-transform lg:translate-x-0 overflow-y-auto ${
            mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
          data-testid="admin-sidebar"
        >
          <div className="p-6 border-b border-aviana-border flex items-center justify-between">
            <div>
              <p className="overline mb-1">Aviana Sanctuary</p>
              <p className="font-display text-2xl text-aviana-ink leading-none">Admin</p>
              {user?.email && (
                <p className="text-[11px] text-aviana-muted mt-1 truncate max-w-[200px]">{user.email}</p>
              )}
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden text-aviana-muted hover:text-aviana-ink"
              data-testid="admin-mobile-menu-close"
            >
              <X size={22} />
            </button>
          </div>

          <nav className="p-4 space-y-6 pb-24" data-testid="admin-sidebar-nav">
            {ADMIN_NAV.map((group) => (
              <div key={group.label}>
                <p className="text-[10px] tracking-[0.22em] uppercase text-aviana-muted/70 mb-2 px-3">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <NavItem key={item.id} item={item} badges={badges} />
                  ))}
                </div>
              </div>
            ))}

            <div className="pt-4 border-t border-aviana-border">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 pl-4 pr-3 py-2 rounded-lg text-[13px] text-aviana-inkSoft hover:bg-red-50 hover:text-red-700 transition-all"
                data-testid="admin-sidebar-logout"
              >
                <LogOut size={15} />
                <span>Sign out</span>
              </button>
            </div>
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 px-4 sm:px-8 lg:px-12 py-8 lg:py-10" data-testid="admin-main">
          {currentSection && (
            <div className="mb-6 hidden lg:flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-aviana-muted">
              <span>{currentSection.group}</span>
              <ChevronRight size={12} />
              <span className="text-aviana-plum">{currentSection.item.label}</span>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
